/**
 * PWA-4 — the worker's offline status line.
 *
 * Three states worth showing, in priority order:
 *   1. Something failed permanently → the worker must be told; their hours did
 *      NOT reach the server and nobody else will notice.
 *   2. Actions waiting to sync → reassurance that the tap was saved.
 *   3. Offline with nothing queued → explain why data may look stale.
 *
 * Renders nothing when online with an empty queue, which is the normal case.
 *
 * UX 1.0: the queue state is one `StatusLine` (text + icon, `role="status"`),
 * and removing an unsent action asks in a `ConsequenceDialog` — never
 * `window.confirm`. A refused START gets the job page's own dialog
 * (`DiscardStartDialog`), so both doors name the same consequences: the
 * start time, the pending finish, and the unsent photos and expenses.
 */

import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { workerErrorText } from '../lib/errorText';
import { useFormatters } from '../lib/useFormatters';
import { workerError } from '../lib/workerError';
import {
  retryFailedActions,
  useSyncQueue,
  discardQueuedAction,
  recoverLegacyQueue,
  type PendingStart,
  type SyncQueueState,
} from '../services/syncQueue';

import { ConsequenceDialog } from './ConsequenceDialog';
import { DiscardStartDialog } from './DiscardStartDialog';
import { useRefusedStartCards } from './refusedStartCard';
import { StatusLine } from './StatusLine';

type QueueIssue = SyncQueueState['issues'][number];

/**
 * The queue holds no zone: an issue's time is read on the legacy site clock
 * (Europe/Tallinn, `workTimeZone`), never the device's — the same instant the
 * job page shows for an EE/FI site.
 */
const QUEUE_ZONE = null;

export function OfflineIndicator() {
  const { t } = useTranslation('worker');
  const fmt = useFormatters();
  const { unowned, issues: allIssues = [], pendingStarts = [] } = useSyncQueue();
  // A6 — a refused start whose job-page card is on screen is resolved THERE
  // (its retry and discard); listing it here too gave the worker two discard
  // paths for one start. The actions queued behind it go with it.
  const cards = useRefusedStartCards();
  const onCard = new Set(
    pendingStarts.filter((p) => p.failed && cards.has(p.id)).map((p) => p.localRef),
  );
  const issues = allIssues.filter(
    (i) =>
      !(i.type === 'startWorkDay' && cards.has(i.id)) && !(i.awaitsRef && onCard.has(i.awaitsRef)),
  );
  // What the dialog is about, fixed when it opens: the queue updates while the
  // discard runs, and the dialog must not change kind under the person.
  const [discarding, setDiscarding] = useState<{
    issue: QueueIssue;
    refusedStart?: PendingStart;
  } | null>(null);
  useEffect(() => {
    if (unowned) void recoverLegacyQueue().catch(() => {});
  }, [unowned]);

  // V1 — a refused start removes what waits behind it; its dialog says so.
  const open = (issue: QueueIssue) =>
    setDiscarding({
      issue,
      refusedStart:
        issue.type === 'startWorkDay'
          ? pendingStarts.find((p) => p.id === issue.id && p.failed)
          : undefined,
    });

  return (
    <>
      {unowned > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('offline.unowned')}
          <Button onClick={() => void recoverLegacyQueue()}>{t('offline.recover')}</Button>
        </Alert>
      )}
      <QueueStatus retryOnCard={cards.size > 0} />
      {issues.map((issue) => (
        <Alert severity={issue.unowned ? 'warning' : 'error'} key={issue.id} sx={{ mb: 1 }}>
          <Stack spacing={1}>
            <Typography>
              {t(`offline.actions.${issue.type}`)} · {fmt.dateTime(issue.createdAt, QUEUE_ZONE)}
            </Typography>
            <Typography>
              {issue.unowned
                ? t('offline.unowned')
                : workerError(t, { details: { reason: issue.errorCode } })}
            </Typography>
            <Typography variant="caption">{t('offline.discardHint')}</Typography>
            <Button onClick={() => open(issue)}>{t('offline.discard')}</Button>
          </Stack>
        </Alert>
      ))}
      {discarding?.refusedStart && (
        <DiscardStartDialog
          open
          start={discarding.refusedStart}
          zone={QUEUE_ZONE}
          onClose={() => setDiscarding(null)}
        />
      )}
      {discarding && !discarding.refusedStart && (
        <ConsequenceDialog
          open
          destructive
          title={t('offline.discardConfirm')}
          description={t('offline.discardHint')}
          consequence={[
            {
              label: t(`offline.actions.${discarding.issue.type}`),
              value: fmt.dateTime(discarding.issue.createdAt, QUEUE_ZONE),
            },
          ]}
          confirmLabel={t('offline.discard')}
          onConfirm={() => discardQueuedAction(discarding.issue.id)}
          onClose={() => setDiscarding(null)}
          errorText={(err) => workerErrorText(t, err)}
        />
      )}
    </>
  );
}

/**
 * `retryOnCard`: a refused-start card is on screen, and its „Proovi uuesti”
 * runs the same `retryFailedActions` — so the line keeps saying what failed
 * but leaves the button to the card (one retry control, A6).
 */
function QueueStatus({ retryOnCard = false }: { retryOnCard?: boolean }) {
  const { t } = useTranslation('worker');
  const { pending, failed, syncing, online } = useSyncQueue();

  if (failed > 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <StatusLine
          state="failed"
          message={t('offline.failed', { count: failed })}
          onRetry={retryOnCard ? undefined : () => void retryFailedActions()}
        />
      </Box>
    );
  }

  if (pending > 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <StatusLine
          state={syncing ? 'working' : 'queued'}
          message={
            syncing
              ? t('offline.syncing', { count: pending })
              : t('offline.pending', { count: pending })
          }
        />
      </Box>
    );
  }

  if (!online) {
    return (
      <Box sx={{ mb: 2 }}>
        <StatusLine state="stale" message={t('offline.offline')} />
      </Box>
    );
  }

  return null;
}
